import {
    getActiveMediaFilters,
    getActiveSearchContextForCurrentTab,
    MEDIA_SOURCE_OPTIONS,
    MEDIA_TYPE_OPTIONS,
    setFilter,
    setSelectedMediaLicenseFilter,
    setSelectedMediaTypeFilter,
} from 'js/src/data/media';
import type { SearchContextValueType } from 'js/src/data/media/media-types';
import { MEDIA_LICENSE_OPTIONS } from 'js/src/data/media/media-types';
import {
    COOKIE_KEYS,
    ImmutableFromJS,
    MAX_RECENTLY_USED_SAVED_TEMPLATES,
} from 'js/src/libs/consts';
import { isTesting } from 'js/src/libs/custom-utils';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';
import Cookies from 'js-cookie';

// Used to force reset a user's cookie to a particular value
// To use, increment the reset value constant and include your reset logic in resetCookie()
const FILTER_COOKIE_RESET_VAL = 3;

export type MediaFilter = MEDIA_TYPE_OPTIONS | MEDIA_SOURCE_OPTIONS | MEDIA_LICENSE_OPTIONS;
type MediaFilterCookie = Record<SearchContextValueType, MediaFilter[]>;

/**
 * Returns the media filter cookie value as an Immutable Map
 */
function getMediaFilterCookie(): MediaFilterCookie {
    let cookie;

    try {
        const cookie_val = Cookies.get(COOKIE_KEYS.MEDIA_FILTER);
        if (cookie_val) {
            cookie = JSON.parse(cookie_val);
        }
    } catch (e) {
        // pass
    }
    // If cookie didn't exist before, create and set a new one
    if (cookie === undefined) {
        cookie = {} as MediaFilterCookie;
        Cookies.set(COOKIE_KEYS.MEDIA_FILTER, JSON.stringify(cookie), {
            expires: 365,
        });
    }
    return cookie;
}

function setMediaFilterCookie(val: MediaFilterCookie) {
    Cookies.set(COOKIE_KEYS.MEDIA_FILTER, JSON.stringify(val), {
        expires: 365,
    });
}

function isMediaSourceOption(value: MediaFilter): value is MEDIA_SOURCE_OPTIONS {
    return Object.values(MEDIA_SOURCE_OPTIONS).includes(value as MEDIA_SOURCE_OPTIONS);
}

function isMediaTypeOption(value: MediaFilter): value is MEDIA_TYPE_OPTIONS {
    return Object.values(MEDIA_TYPE_OPTIONS).includes(value as MEDIA_TYPE_OPTIONS);
}

function isMediaLicenseOption(value: MediaFilter): value is MEDIA_LICENSE_OPTIONS {
    return Object.values(MEDIA_LICENSE_OPTIONS).includes(value as MEDIA_LICENSE_OPTIONS);
}

/**
 * If needed, reset the media filter cookie to a custom value
 */
export function loadMediaFilterCookie(canUseEditorialMedia: boolean) {
    return (dispatch: AppDispatch) => {
        // first, reset the cookie if we've changed formats since it was first set up
        try {
            // If the reset value has been incremented since last storing a cookie value,
            // ensure the marketplace filter is enabled for all relevant categories
            const cookie_val = Cookies.get(COOKIE_KEYS.MEDIA_FILTER);
            if (cookie_val !== undefined) {
                const resetValue = parseInt(cookie_val);
                if (resetValue < FILTER_COOKIE_RESET_VAL) {
                    setMediaFilterCookie({} as MediaFilterCookie);
                }
            }
        } catch (e) {
            // If there's no reset value stored, do nothing
        }
        Cookies.set(COOKIE_KEYS.MEDIA_FILTER_RESET, FILTER_COOKIE_RESET_VAL.toString(), {
            expires: 365,
        });

        // now load the media filter cookie
        const filterVal = getMediaFilterCookie();
        Object.keys(filterVal).forEach((key) => {
            const search_context = key as SearchContextValueType;
            // first load the type – we're only expecting a single value here
            const typeValues = filterVal[search_context].filter(isMediaTypeOption);
            if (typeValues.length === 1) {
                dispatch(
                    setSelectedMediaTypeFilter({
                        search_context,
                        selected_type: typeValues[0],
                    }),
                );
            }
            // load the source filters (if some are present that means any that aren't present are disabled)
            const sourceValues = filterVal[search_context].filter(isMediaSourceOption);
            if (sourceValues.length > 0) {
                const val: Partial<Record<MEDIA_SOURCE_OPTIONS, boolean>> = {};
                Object.values(MEDIA_SOURCE_OPTIONS).forEach((source) => {
                    val[source] = sourceValues.includes(source);
                });
                dispatch(setFilter({ search_context, filter: ImmutableFromJS(val) }));
            }
            // load the licensing filters (if some are present that means any that aren't present are disabled)
            const licensingValues = filterVal[search_context].filter(isMediaLicenseOption);
            // temporary: set current users with old cookie format without licensing values to match the default value of ALL
            if (licensingValues.length === 0 || licensingValues.length > 1) {
                if (canUseEditorialMedia) {
                    dispatch(
                        setSelectedMediaLicenseFilter({
                            search_context,
                            selected_license_type: MEDIA_LICENSE_OPTIONS.ALL,
                        }),
                    );
                } else {
                    dispatch(
                        setSelectedMediaLicenseFilter({
                            search_context,
                            selected_license_type: MEDIA_LICENSE_OPTIONS.STOCK,
                        }),
                    );
                }
            } else if (licensingValues.length === 1) {
                if (canUseEditorialMedia) {
                    dispatch(
                        setSelectedMediaLicenseFilter({
                            search_context,
                            selected_license_type: licensingValues[0],
                        }),
                    );
                } else {
                    dispatch(
                        setSelectedMediaLicenseFilter({
                            search_context,
                            selected_license_type: MEDIA_LICENSE_OPTIONS.STOCK,
                        }),
                    );
                }
            }
        });
    };
}

export function saveMediaFilterToCookie() {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const cookieData = getMediaFilterCookie();
        const searchContext = getActiveSearchContextForCurrentTab(getState());
        if (!searchContext) throw new Error('No search context found for current tab');
        cookieData[searchContext] = getActiveMediaFilters(getState(), searchContext);
        setMediaFilterCookie(cookieData);
    };
}

export const videoLimitWarningWasShown = (): boolean => {
    return Cookies.get(COOKIE_KEYS.VIDEO_LIMIT_WARNING) === 'true';
};

export const setVideoLimitWarningWasShown = () => {
    Cookies.set(COOKIE_KEYS.VIDEO_LIMIT_WARNING, 'true', { expires: 365 });
};

export const setCaptionsCookie = (visible: boolean) => {
    Cookies.set(COOKIE_KEYS.CAPTIONS, JSON.stringify({ visible }), {
        expires: 365,
    });
};

// Returns undefined if captions cookies has not been set
export const getCaptionsCookie = (): boolean | undefined => {
    const captionsCookie = Cookies.get(COOKIE_KEYS.CAPTIONS);
    if (!captionsCookie) {
        return undefined;
    }
    return JSON.parse(captionsCookie).visible;
};

const getRecentlyUsedBlueprintIdsKey = () => {
    if (isTesting()) {
        return `${COOKIE_KEYS.RECENTLY_USED_BLUEPRINT_IDS}_${-1}_${-1}`;
    }
    return `${COOKIE_KEYS.RECENTLY_USED_BLUEPRINT_IDS}_${SERVER.USER.id}_${SERVER.USER.workspace_id}`;
};

export const getWorkspaceRecentlyUsedBlueprintIds = (): number[] => {
    const cookieKey = getRecentlyUsedBlueprintIdsKey();
    return JSON.parse(Cookies.get(cookieKey) || '[]');
};

export const prependRecentlyUsedBlueprintIds = (blueprintId: number) => {
    const cookieKey = getRecentlyUsedBlueprintIdsKey();
    if (!Number.isInteger(blueprintId)) {
        return;
    }
    const blueprintIds = getWorkspaceRecentlyUsedBlueprintIds();
    const updatedBlueprintIds = [
        blueprintId,
        ...blueprintIds.filter((id) => id !== blueprintId),
    ].slice(0, MAX_RECENTLY_USED_SAVED_TEMPLATES);
    Cookies.set(cookieKey, JSON.stringify(updatedBlueprintIds), {
        expires: 365,
    });
};

export const removeRecentlyUsedBlueprintId = (blueprintId: number) => {
    const cookieKey = getRecentlyUsedBlueprintIdsKey();
    const blueprintIds = getWorkspaceRecentlyUsedBlueprintIds();
    const updatedBlueprintIds = blueprintIds.filter((id) => id !== blueprintId);
    Cookies.set(cookieKey, JSON.stringify(updatedBlueprintIds), {
        expires: 365,
    });
};

export interface WorkspaceMediaModeConfig {
    mode: string;
    selectedFolderIds: number[];
}

export const getWorkspaceMediaModeConfig = (): WorkspaceMediaModeConfig => {
    const defaults: WorkspaceMediaModeConfig = {
        mode: 'recommended',
        selectedFolderIds: [],
    };

    try {
        const cookieValue = Cookies.get(COOKIE_KEYS.WORKSPACE_MEDIA_MODE);
        if (cookieValue) {
            const parsed = JSON.parse(cookieValue);
            return {
                mode: typeof parsed.mode === 'string' ? parsed.mode : defaults.mode,
                selectedFolderIds: Array.isArray(parsed.selectedFolderIds)
                    ? parsed.selectedFolderIds
                    : defaults.selectedFolderIds,
            };
        }
    } catch (e) {
        // Fall through to defaults
    }

    return defaults;
};

export const setWorkspaceMediaModeConfig = (config: WorkspaceMediaModeConfig) => {
    Cookies.set(COOKIE_KEYS.WORKSPACE_MEDIA_MODE, JSON.stringify(config), {
        expires: 365,
    });
};
